import { useEffect, useId, useRef } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useEscapeKey } from '../../../../../hooks/useEscapeKey';

interface DiscardMarksConfirmProps {
    leavingTo: 'photos' | 'composer';
    onKeep: () => void;
    onDiscard: () => void;
}

// Stacks over the markup dialog (z-[60]), so it sits one layer higher.
function DiscardMarksConfirm({ leavingTo, onKeep, onDiscard }: DiscardMarksConfirmProps) {
    const titleId = useId();
    const keepRef = useRef<HTMLButtonElement>(null);
    useEscapeKey(onKeep, true);

    useEffect(() => {
        keepRef.current?.focus();
    }, []);

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-6">
            <div
                role="alertdialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className="w-full max-w-[420px] rounded-xl bg-white p-6 shadow-xl"
            >
                <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0">
                        <ExclamationTriangleIcon className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                        <h3 id={titleId} className="text-base font-semibold text-gray-900">
                            Discard your marks?
                        </h3>
                        <p className="mt-1 text-sm text-gray-600">
                            {leavingTo === 'photos'
                                ? 'Going back to the photos removes the marks on this one.'
                                : 'Closing removes the marks on this photo. Nothing is attached.'}
                        </p>
                    </div>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                    <button
                        ref={keepRef}
                        type="button"
                        onClick={onKeep}
                        className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Keep editing
                    </button>
                    <button
                        type="button"
                        onClick={onDiscard}
                        className="px-4 py-2 text-sm font-semibold text-red-600 bg-white border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
                    >
                        Discard
                    </button>
                </div>
            </div>
        </div>
    );
}

export { DiscardMarksConfirm };
